import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CoreModule } from 'src/app/core/core.module';
import { InventoryItemImageSlot } from 'src/app/core/models/inventory/inventory-item-image-slot.interface';

@Component({
  selector: 'app-inventory-item-image-viewer',
  templateUrl: './inventory-item-image-viewer.component.html',
  styleUrl: './inventory-item-image-viewer.component.scss',
  standalone: true,
  imports: [CommonModule, CoreModule],
})
export class InventoryItemImageViewerComponent implements OnChanges {
  @Input() public isOpen = false;
  @Input() public title = '';
  @Input() public images: InventoryItemImageSlot[] = [];
  @Input() public activeImageId = '';

  @Output() public closed = new EventEmitter<void>();

  public activeImageIndex = 0;
  public currentImage: InventoryItemImageSlot | null = null;
  public zoomLevel = 1;
  public currentTransform = 'scale(1)';
  public canGoPrevious = false;
  public canGoNext = false;
  public canZoomIn = true;
  public canZoomOut = false;

  private readonly minZoom = 1;
  private readonly maxZoom = 3;
  private readonly zoomStep = 0.25;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['images'] || changes['activeImageId'] || changes['isOpen']) {
      this.syncViewerState();
    }
  }

  public showPrevious(): void {
    if (!this.canGoPrevious) {
      return;
    }

    this.activeImageIndex -= 1;
    this.syncCurrentImage();
  }

  public showNext(): void {
    if (!this.canGoNext) {
      return;
    }

    this.activeImageIndex += 1;
    this.syncCurrentImage();
  }

  public selectImage(imageId: string): void {
    const nextIndex = this.images.findIndex((image) => image.id === imageId);
    if (nextIndex < 0) {
      return;
    }

    this.activeImageIndex = nextIndex;
    this.syncCurrentImage();
  }

  public zoomIn(): void {
    this.setZoom(this.zoomLevel + this.zoomStep);
  }

  public zoomOut(): void {
    this.setZoom(this.zoomLevel - this.zoomStep);
  }

  public resetZoom(): void {
    this.setZoom(this.minZoom);
  }

  private syncViewerState(): void {
    if (this.images.length === 0) {
      this.currentImage = null;
      this.activeImageIndex = 0;
      this.resetZoom();
      this.syncNavigation();
      return;
    }

    const matchedIndex = this.images.findIndex((image) => image.id === this.activeImageId);
    this.activeImageIndex = matchedIndex >= 0 ? matchedIndex : 0;
    this.syncCurrentImage();
  }

  private syncCurrentImage(): void {
    this.currentImage = this.images[this.activeImageIndex] ?? null;
    this.resetZoom();
    this.syncNavigation();
  }

  private syncNavigation(): void {
    this.canGoPrevious = this.activeImageIndex > 0;
    this.canGoNext = this.activeImageIndex < this.images.length - 1;
  }

  private setZoom(value: number): void {
    const normalizedZoom = Math.min(this.maxZoom, Math.max(this.minZoom, Number(value.toFixed(2))));
    this.zoomLevel = normalizedZoom;
    this.currentTransform = `scale(${normalizedZoom})`;
    this.canZoomIn = normalizedZoom < this.maxZoom;
    this.canZoomOut = normalizedZoom > this.minZoom;
  }
}
